import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import merge from 'lodash.merge';

import { Button, Col, Container, Div, Icon, Input, Row, Text } from '@pinpoint/design-kit';

import { Style1 as Style } from 'src/styles';

import Lift from 'src/charts/lift';
import { withFixedTitle } from 'src/components';

const SquishlessLift = withFixedTitle(Lift);

/* eslint-disable react/prop-types */
/* eslint-disable no-console */

const Copy = {
  heading: {
    title: 'Predictive Model Results',
    numberOfUsers: 'Total Number of Users',
    engagedUsers: 'Engaged Users',
  },
  leftPanel: {
    title: '',
    chart: {
      xAxis: ['Decile (Lowest to Highest Probability of Engagement)'],
      yAxis: ['Lift', 'HIT persons as percentage of bucket'],
      series: ['Lift', '% of HIT persons'],
    },
  },
  rightPanel: {
    topPanel: {
      paragraph: [
        'This visualization shows the analyzed data organized into decile groups (each group represents 10.0% of the Users) according to their probability of engagement as determined by our personality-based predictive model.',
        'The Lift value for each group represents how many times more likely are its users to engage when compared to the average user in the dataset.',
      ],
    },
    bottomPanel: {
      title: 'Interactive Lift Calculator',
      paragraph: [
        'Instructions: Enter the average percentage of HIT persons in the entire dataset. For example, if the average cancellation rate is 15%, enter 15. The bar chart will then calculate the percentage of HIT persons in each percentile bucket.',
      ],
    },
  },
};

function HoldableButton({ onClick, value, min, max, ...rest }) {
  const [intervalEvent, setIntervalEvent] = useState(null);

  useEffect(() => {
    if (min) {
      if (value <= min && intervalEvent) {
        clearInterval(intervalEvent);
      }
    }
    if (max) {
      if (value >= max && intervalEvent) {
        clearInterval(intervalEvent);
      }
    }
  }, [value, min, max]);

  return (
    <Button
      {...rest}
      onClick={() => {
        onClick();
      }}
      onMouseDown={() => {
        const interval = setInterval(() => {
          onClick();
        }, 500);
        setIntervalEvent(interval);
      }}
      onMouseUp={() => {
        if (intervalEvent) clearInterval(intervalEvent);
      }}
      onMouseLeave={() => {
        if (intervalEvent) clearInterval(intervalEvent);
      }}
    />
  );
}

function NumberStepper({ value: baseValue, onIncrease, onDecrease, onChange, min, max }) {
  const [isActive, setActive] = useState(false);
  const [value, setValue] = useState(baseValue.toFixed(2));

  useEffect(() => {
    setValue(baseValue.toFixed(2));
  }, [baseValue]);

  return (
    <Div d="flex" p={{ y: '1rem' }} m={{ x: '-1rem' }}>
      <HoldableButton
        bg={parseFloat(value) <= min ? 'tabRest' : 'brand500'}
        hoverBg={parseFloat(value) <= min ? 'tabSelected' : 'brand600'}
        cursor={parseFloat(value) <= min ? 'not-allowed' : 'pointer'}
        m={{ x: '1rem' }}
        data-tip="Press and hold the button to decrease"
        value={value}
        min={min}
        max={max}
        onClick={() => {
          onDecrease();
        }}
      >
        <Icon name="Minus" color="white" size="1rem" viewBox="0 0 24 24" />
      </HoldableButton>
      <Input
        w="8rem"
        value={isActive ? value : parseFloat(value).toFixed(2)}
        onFocus={() => {
          setActive(true);
        }}
        onChange={(e) => {
          if (isActive) {
            setValue(e.target.value);
          }
        }}
        onBlur={() => {
          onChange(parseFloat(value));
          setActive(false);
        }}
        onKeyPress={(event) => {
          if (event.key === 'Enter') {
            onChange(parseFloat(value));
          }
        }}
        style={{
          textAlign: 'center',
        }}
      />
      <HoldableButton
        bg={parseFloat(value) >= max ? 'gray500' : 'brand500'}
        hoverBg={parseFloat(value) >= max ? 'gray500' : 'brand300'}
        cursor={parseFloat(value) >= max ? 'not-allowed' : 'pointer'}
        m={{ x: '1rem' }}
        data-tip="Press and hold the button to increase"
        value={value}
        min={min}
        max={max}
        onClick={() => {
          onIncrease();
        }}
      >
        <Icon name="Plus" color="white" size="1rem" />
      </HoldableButton>
    </Div>
  );
}

/* eslint-enable react/prop-types */

function ThinkalikeModel({
  minH,
  minW,
  totalUsers,
  engagedUsers,
  riskPercentage,
  percentile,
  lift,
  options,
  styleOverride,
  copyOverride,
}) {
  const baseEngagementShare = engagedUsers / totalUsers;
  const baseEngagementPercentage = 100 * baseEngagementShare;
  const [engagementPercentage, setEngagementPercentage] = useState(riskPercentage || baseEngagementPercentage);
  const engagementValues = lift.map((l) => l * engagementPercentage);
  const quantum = useMemo(() => {
    if (riskPercentage) {
      return riskPercentage < 1 ? riskPercentage * 1 : Math.floor(riskPercentage / 1) * 1;
    }
    return baseEngagementPercentage < 1 ? baseEngagementPercentage * 1 : Math.floor(baseEngagementPercentage / 1) * 1;
  }, [riskPercentage, baseEngagementPercentage]);
  const maxEngagement = Math.floor(Math.max(...engagementValues) / quantum) * quantum + quantum;
  const maxLift = maxEngagement / engagementPercentage;
  const mergedCopy = merge(Copy, copyOverride);
  return (
    <Container
      h="100%"
      minH={minH}
      w="100%"
      minW={minW}
      bg="secondary"
      style={{
        paddingLeft: '0rem',
        paddingRight: '0rem',
        ...styleOverride,
      }}
    >
      <Row p={{ t: '1rem', b: '0.5rem' }} minH="calc(600px + 1.5rem)" h="calc(100% - 1.5rem)">
        <Col size={{ xs: '12', lg: '8' }} h="100%" gutterWidth="1.25rem">
          <Div minH="600px" h="100%" w="100%" rounded="md" style={styleOverride} m={{ y: '1rem' }}>
            <SquishlessLift
              title={mergedCopy.leftPanel.title}
              labels={{
                values: percentile,
              }}
              liftSeries={{
                name: mergedCopy.leftPanel.chart.series[0],
                values: lift,
              }}
              engagementSeries={{
                name: mergedCopy.leftPanel.chart.series[1],
                values: engagementValues,
              }}
              maxEngagement={maxEngagement}
              maxLift={maxLift}
              copyOverride={mergedCopy.leftPanel.chart}
              options={options}
            />
          </Div>
        </Col>
        <Col size={{ xs: '12', lg: '4' }} h="100%" gutterWidth="1.25rem">
          <Div
            minH="250px"
            h="100%"
            w="100%"
            rounded="md"
            p={{ t: '2.5rem', b: '2rem', x: '1.5rem' }}
            d="flex"
            flexDir="column"
            bg="secondary"
          >
            <Div d="flex" justify="center">
              <Text fontFamily="secondary" textSize="title" textWeight="400" p={{ y: '0.25rem' }} tag="span">
                {mergedCopy.rightPanel.bottomPanel.title}
              </Text>
            </Div>
            <Div d="flex" justify="center" m={{ b: '1rem' }}>
              <NumberStepper
                value={engagementPercentage}
                onChange={(value) => {
                  setEngagementPercentage(value);
                }}
                onIncrease={() => {
                  setEngagementPercentage((value) => Math.min(100, value + 0.1));
                }}
                onDecrease={() => {
                  setEngagementPercentage((value) => Math.max(0, value - 0.1));
                }}
                min={0}
                max={100}
              />
            </Div>
            <Text textWeight="300" textSize="subheader">
              {mergedCopy.rightPanel.bottomPanel.paragraph}
            </Text>
          </Div>
        </Col>
      </Row>
    </Container>
  );
}

ThinkalikeModel.propTypes = {
  minH: PropTypes.string,
  minW: PropTypes.string,
  totalUsers: PropTypes.number.isRequired,
  engagedUsers: PropTypes.number.isRequired,
  riskPercentage: PropTypes.number.isRequired,
  percentile: PropTypes.arrayOf(PropTypes.number).isRequired,
  lift: PropTypes.arrayOf(PropTypes.number).isRequired,
  options: PropTypes.shape({}),
  styleOverride: PropTypes.shape({}),
  copyOverride: PropTypes.shape({}),
};

ThinkalikeModel.defaultProps = {
  minH: '100%',
  minW: '100%',
  styleOverride: {
    background: Style.global.backgroundColor,
  },
  copyOverride: {},
  options: {},
};

export default ThinkalikeModel;
